import dayjs from 'dayjs';
import { IJobRunReport } from '@components/sources/sourceDetails/retlSyncs/recent/context';
import { ISourceStore } from '@stores/source';
import { IDestinationStore } from '@stores/destination';
import { RetlConnectionStore } from '@stores/retlConnections/retlConnection';

export enum TabValue {
  LATEST = 'latest',
  PAST = 'past',
}

export enum SyncConfigStatus {
  STARTING = 'starting',
  SYNCING = 'in progress',
  STOPPING = 'stopping',
  PAUSED = 'paused',
  AVAILABLE_TO_SYNC = 'available_to_sync',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  ABORTED = 'aborted',
  IDLE = 'idle',
}

export enum SyncType {
  INCREMENTAL = 'incremental',
  FULL = 'full',
}

export type DateRange = { from: dayjs.Dayjs; to: dayjs.Dayjs };

export interface ILatestSyncStore {
  loadingSyncData: boolean;
  loadingSyncTrend: boolean;
  latestSync: SyncData | null;
  syncConfigStatus: SyncConfigStatus;
  isSetupCompleted: boolean;
  syncTrend: SyncTrend | null;
  errorInSyncTrend: boolean;
  isEligibleForGraph: boolean;
  isGraphAvailable: boolean;
  load: () => void;
  stopPolling: () => void;
  startSync: (syncType: SyncType) => void;
  stopSync: () => void;
  refreshSyncTrend: () => void;
  checkConnectionStatus: () => void;
  reset: () => void;
}

export interface IPastSyncsStore {
  loading: boolean;
  dateRange: DateRange;
  pageNumber: number;
  totalRuns: number;
  pastSyncs: SyncData[];
  onDateRangeChange: (dateRange: DateRange) => void;
  onPageNumberChange: (pageNumber: number) => void;
  load: () => void;
  reset: () => void;
}

export interface IRetlSyncsStore {
  source: ISourceStore | undefined;
  destination: IDestinationStore | undefined;
  connection: RetlConnectionStore | undefined;
  syncMode: string;
  tab: TabValue;
  latestSyncStore: ILatestSyncStore;
  pastSyncsStore: IPastSyncsStore;
  sampleErrorsStore: ISampleErrorsStore | null;
  setTab: (tab: TabValue) => void;
  setConnectionDetails: (sourceId: string, destinationId: string) => void;
  setSampleErrorStore: (sync: SyncData, type: ErrorType) => void;
  resetSampleErrorStore: () => void;
  reset: () => void;
}

export interface SyncData extends Omit<IJobRunReport, 'status' | 'started_at' | 'finished_at'> {
  status:
    | SyncConfigStatus.SUCCEEDED
    | SyncConfigStatus.FAILED
    | SyncConfigStatus.ABORTED
    | SyncConfigStatus.SYNCING;
  duration: string;
  started_at: dayjs.Dayjs;
  finished_at: dayjs.Dayjs;
}

export interface RawSyncTrend {
  timestamps: number[];
  states: {
    succeeded: number[];
    retries: {
      '429': number[];
      '5xx': number[];
    };
    aborted: number[];
    dropped: number[];
  };
}

export enum GraphTabValue {
  SYNCED = 'synced',
  RETRYING = 'retrying',
}

export interface SyncTrendTabData {
  chartData: {
    bucket: number;
    charts: (number | null)[];
  }[];
  labels: string[];
  stepSize: number;
}

export interface SyncTrend {
  [GraphTabValue.SYNCED]: SyncTrendTabData;
  [GraphTabValue.RETRYING]: SyncTrendTabData;
}

export enum ErrorType {
  INVALID = 'sources',
  FAILED = 'server',
  ABORTED = 'aborted',
}

export type InvalidRow = Record<string, unknown>;

export type SampleError = {
  count: number;
  sampleEvent: Record<string, unknown> & { invalid?: InvalidRow[] };
  sampleResponse: string;
  statusCode: number;
};

export interface ISampleErrorsStore {
  loading: boolean;
  syncData: SyncData;
  errorType: ErrorType;
  sampleErrors: SampleError[];
  selectedError: SampleError | null;
  sampleErrorCollapsed: boolean;
  sampleEventCollapsed: boolean;
  setSelectedError: (error: SampleError | null) => void;
  setSampleErrorCollapsed: (value: boolean) => void;
  setSampleEventCollapsed: (value: boolean) => void;
  fetchSyncErrors: () => void;
}
